import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Copy, GripVertical, Trash2, Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/components/form-builder/FormBuilder';

interface SortableFieldProps {
  field: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

const SortableField: React.FC<SortableFieldProps> = ({
  field,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
}) => {
  const { t } = useTranslation('common');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const renderPreview = () => {
    const baseClass = 'w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400';
    if (field.fieldType === 'textarea') {
      return <textarea className={baseClass} rows={4} disabled placeholder={field.fieldPlaceholder} />;
    }
    if (field.fieldType === 'select') {
      return (
        <select className={baseClass} disabled>
          <option>{field.fieldPlaceholder || t('formBuilder.canvas.selectOption', { defaultValue: 'Select an option' })}</option>
          {field.options?.map((opt, idx) => <option key={idx}>{opt}</option>)}
        </select>
      );
    }
    if (field.fieldType === 'checkbox' || field.fieldType === 'radio') {
      return (
        <div className="space-y-2">
          {field.options?.map((opt, idx) => (
            <label key={idx} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
              <input type={field.fieldType} disabled />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }
    return <input type={field.fieldType} className={baseClass} disabled placeholder={field.fieldPlaceholder} />;
  };

  return (
    <article
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={onSelect}
      className={`group relative cursor-pointer overflow-hidden rounded-[12px] border bg-white p-6 shadow-sm transition dark:bg-gray-900 ${
        isDragging ? 'opacity-50' : 'opacity-100'
      } ${
        isSelected
          ? 'border-brand-500 shadow-[0_18px_40px_-30px_rgba(226,0,116,0.45)] dark:border-brand-400'
          : 'border-slate-200 hover:border-slate-300 dark:border-gray-800 dark:hover:border-slate-700'
      }`}
    >
      <div className={`absolute inset-x-0 top-0 h-1 ${isSelected ? 'bg-brand-500' : 'bg-transparent group-hover:bg-brand-200 dark:group-hover:bg-brand-500/30'}`} />

      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-gray-500">
            <button
              {...listeners}
              className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-400 hover:text-slate-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-500 dark:hover:text-slate-300"
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
            <span>{field.fieldType}</span>
            {field.conditional && (
              <span className="rounded-full bg-brand-50 px-2 py-1 text-brand-600 dark:bg-brand-500/10 dark:text-brand-300">
                {t('formBuilder.canvas.conditional', { defaultValue: 'Conditional' })}
              </span>
            )}
          </div>
          <h3 className="break-words text-[clamp(1.25rem,4vw,1.75rem)] font-normal leading-tight text-slate-950 dark:text-white">
            {field.fieldLabel}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </h3>
          <p className="mt-1 text-sm text-slate-400 dark:text-gray-500">
            {field.fieldPlaceholder || t('formBuilder.canvas.noPlaceholder', { defaultValue: 'No helper text yet' })}
          </p>
        </div>

        <div className="flex items-center gap-2 opacity-0 transition group-hover:opacity-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:border-brand-300 hover:text-brand-600 dark:border-gray-700 dark:text-gray-300 dark:hover:border-brand-500/40 dark:hover:text-brand-300"
            title={t('formBuilder.canvas.duplicate', { defaultValue: 'Duplicate' })}
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:border-red-300 hover:text-red-600 dark:border-gray-700 dark:text-gray-300 dark:hover:border-red-500/40 dark:hover:text-red-300"
            title={t('formBuilder.canvas.delete', { defaultValue: 'Delete' })}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {renderPreview()}
    </article>
  );
};

interface FormCanvasProps {
  fields: FormField[];
  selectedFieldId: string | null;
  onFieldSelect: (id: string) => void;
  onFieldDelete: (id: string) => void;
  onFieldDuplicate: (id: string) => void;
}

const FormCanvas: React.FC<FormCanvasProps> = ({
  fields,
  selectedFieldId,
  onFieldSelect,
  onFieldDelete,
  onFieldDuplicate,
}) => {
  const { t } = useTranslation('common');
  const { setNodeRef, isOver } = useDroppable({ id: 'form-canvas' });

  return (
    <div
      ref={setNodeRef}
      className={`rounded-[12px] border border-transparent p-0 transition ${
        isOver
          ? 'bg-brand-100/40 dark:bg-brand-500/5'
          : 'border-slate-300 bg-slate-50/70 dark:border-gray-700 dark:bg-gray-900/40'
      }`}
    >
      {fields.length === 0 ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-[12px] border border-dashed border-slate-300 bg-white px-6 py-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-3xl bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-300">
            <Wand2 className="h-8 w-8" />
          </div>
          <h3 className="text-2xl font-semibold text-slate-950 dark:text-white">
            {t('formBuilder.canvas.emptyTitle', { defaultValue: 'Start your form' })}
          </h3>
          <p className="mt-3 max-w-md text-sm leading-7 text-slate-500 dark:text-gray-400">
            {t(
              'formBuilder.canvas.emptyDescription',
              { defaultValue: 'Drag a block from the left side to create your first question. The layout is centered so the form already feels close to the public experience.' }
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-4 rounded-[12px] border border-dashed border-slate-300 p-4 dark:border-gray-700">
          {fields.map((field) => (
            <SortableField
              key={field.id}
              field={field}
              isSelected={selectedFieldId === field.id}
              onSelect={() => onFieldSelect(field.id)}
              onDelete={() => onFieldDelete(field.id)}
              onDuplicate={() => onFieldDuplicate(field.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FormCanvas;
